import { useSelector } from 'react-redux';

import Avatar from '@components/avatar/Avatar';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import ThreeDotActionButton from '@components/three-dot-action-button/ThreeDotActionButton';
import { FEATURE_FLAGS } from '@constants/feature';
import UserPermissionTypes from '@constants/permissions';
import { PaymentCollectionType } from '@features/group-management/constants';
import { RoleType } from '@features/team-management/constants';
import useCheckPermission from '@hooks/checkPermission';
import { Group } from '@types';

import Icon from '../icon/Icon';
// import ProgressBar from '../progress-bar/ProgressBar';
import Tooltip from '../tooltip/Tooltip';
import { getGroupCardColor, getGroupCardTextColor } from './utils';

import theme from '../../../themes/colors';

interface GroupCardProps {
  groupCardId?: string;
  groupDetails: Group;
  className?: string;
  handleGroupCardClick: () => void;
  onClickEdit: (e: React.MouseEvent) => void;
  onClickDuplicate: (e: React.MouseEvent, group: Group) => void;
}

const GroupCard = ({
  groupCardId,
  groupDetails,
  className,
  handleGroupCardClick,
  onClickEdit,
  onClickDuplicate
}: GroupCardProps) => {
  const { checkPermission } = useCheckPermission();
  const { orgFeatureFlags } = useSelector((state: any) => state.user);

  const hasGroupEditAccess = checkPermission([UserPermissionTypes.GROUP_EDIT]);

  const { currentOrg } = useSelector((state: any) => state.user);
  const isAttendanceManager = currentOrg.role === RoleType.ATTENDANCE_MANAGER;

  const showEditGroupIcon =
    hasGroupEditAccess &&
    !(
      groupDetails.interval === PaymentCollectionType.ONCE &&
      !groupDetails.is_active &&
      groupDetails.end_date &&
      new Date(groupDetails.end_date).setHours(0, 0, 0, 0) <
        new Date().setHours(0, 0, 0, 0)
    );

  const renderGroupLogo = () => {
    const bgColor = !groupDetails.is_active
      ? theme.cofeeGreyBlue75
      : getGroupCardColor(groupDetails.id);
    const textColor = getGroupCardTextColor(bgColor);

    return (
      <Avatar
        name={groupDetails.name}
        imageUrl={groupDetails.image_url}
        className={`h-38.5 w-full rounded-lg !border-none ${
          !groupDetails.is_active && groupDetails.image_url ? 'opacity-35' : ''
        }`}
        imageClassName="rounded-lg"
        textClassName="!text-76"
        textColor={textColor}
        bgColor={bgColor}
      />
    );
  };

  const renderGroupName = () => (
    <div className="flex h-12 flex-col justify-between gap-1">
      <div
        className={`truncate break-words text-base font-medium ${
          !groupDetails.is_active ? 'text-cofeeNeutral6' : 'text-cofeeNeutral10'
        }`}
      >
        {groupDetails.name}
      </div>
      <div
        className={`text-sm font-normal ${
          !groupDetails.is_active ? 'text-cofeeNeutral6' : 'text-cofeeNeutral8'
        }`}
      >
        {(groupDetails.active_member_count ?? 0) === groupDetails.member_count
          ? `${groupDetails.active_member_count ?? 0} ${
              groupDetails.active_member_count === 1
                ? 'Active Member'
                : 'Active Members'
            }`
          : `${groupDetails.active_member_count ?? 0}/${
              groupDetails.member_count
            } Active Members`}
      </div>
    </div>
  );

  const renderGroupCreationIncompleteWarning = () => (
    <>
      <Icon
        name="warning"
        size="24"
        data-tooltip-id={`incomplete-group-info-${groupDetails.id}`}
      />
      <Tooltip
        id={`incomplete-group-info-${groupDetails.id}`}
        place="bottom"
        className="max-w-67.5"
      >
        <div className="flex flex-col items-center justify-center gap-2 font-normal">
          <div className="text-sm font-medium text-white">
            Group creation incomplete
          </div>
          <div className="text-xs text-grey40">
            Please review and complete all required fields to finish group
            creation.
          </div>
        </div>
      </Tooltip>
    </>
  );

  const renderIncativeTag = () => (
    <div className="absolute right-0 top-4 z-10">
      <div className="flex items-center gap-2 rounded-bl-[4px] rounded-tl-xl border border-cofeeGreyBlue300 bg-cofeeGreyBlue75 px-4 py-1 text-sm font-medium text-cofeeGreyBlue500 shadow-boxShadow2">
        <div className="size-1.5 min-w-1.5 rounded-full bg-cofeeGreyBlue500" />
        Inactive
      </div>
    </div>
  );

  const getActions = (group: Group) => {
    const actions = [
      {
        id: `edit-${group.id}`,
        label: 'Edit',
        onClick: (e: React.MouseEvent) => onClickEdit(e)
      },
      orgFeatureFlags &&
        orgFeatureFlags.includes(FEATURE_FLAGS.CLONE_GROUP) && {
          id: `duplicate-${group.id}`,
          label: 'Duplicate',
          onClick: (e: React.MouseEvent) => onClickDuplicate(e, group)
        }
    ].filter(Boolean);

    return { actions };
  };

  return (
    <div
      data-testid={groupCardId}
      className={`relative min-h-68 w-full rounded-2xl border border-cofeeNeutral4 bg-cofeeNeutral1 shadow-boxShadow3 ${
        className || ''
      }`}
      onClick={handleGroupCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleGroupCardClick();
        }
      }}
    >
      {!groupDetails.is_active && renderIncativeTag()}
      <div className="m-3">{renderGroupLogo()}</div>
      <div className="mx-3">
        <div className="flex items-start justify-between">
          <div className="max-w-[80%]">
            {renderGroupName()}
            {!groupDetails.amount && renderGroupCreationIncompleteWarning()}
          </div>
          {showEditGroupIcon && (
            <ThreeDotActionButton
              id={groupDetails.id ?? ''}
              actions={getActions(groupDetails).actions}
              isDisabled={!groupDetails.is_active}
              source="Events Card"
              modalStyle="!w-35"
              stroke={theme.cofeeNeutral8}
            />
          )}
        </div>
      </div>
      {!isAttendanceManager && (
        <div
          className={`mt-3 flex items-center justify-between border-t border-cofeeNeutral5 p-3 text-sm font-normal ${
            !groupDetails.is_active
              ? 'text-cofeeNeutral6'
              : 'text-cofeeNeutral8'
          }`}
        >
          <div className="flex items-center gap-1">
            <div
              className={`size-2 min-w-2 rounded-full ${
                !groupDetails.is_active ? 'bg-cofeeNeutral6' : 'bg-cofeeRed6'
              }`}
            />
            Total Due Amount
          </div>
          <CurrencyAmount
            value={Number(groupDetails.pay_due)}
            className={`gap-0.5 text-sm font-normal ${
              !groupDetails.is_active
                ? 'text-cofeeNeutral6'
                : 'text-cofeeNeutral10'
            }`}
          />
        </div>
      )}
    </div>
  );
};
export default GroupCard;
